import type { Path, Point, TElement, TRange } from '@platejs/slate';
import { NodeApi, RangeApi } from 'platejs';
import type { PlateEditor } from 'platejs/react';
import { createPlatePlugin } from 'platejs/react';

/**
 * Robust handling of `beforeinput` events with
 * `inputType: 'insertReplacementText'` — the events browser writing
 * extensions (Grammarly, LanguageTool, platform spellcheck) dispatch when the
 * user accepts a suggestion. Grammarly recognises Slate editors by the
 * `data-slate-editor` attribute and applies corrections through exactly these
 * events.
 *
 * ## Why slate's default handling corrupts content
 *
 * slate-react routes any insertReplacementText event that carries a
 * `DataTransfer` through the full paste pipeline (`insertData`), and resolves
 * the event's target range through DOM↔model WeakMaps:
 *
 * 1. When several replacements arrive in one frame (Grammarly's sentence /
 *    paragraph rewrites apply multiple fixes back-to-back), the first edit
 *    updates the model but React has not re-rendered, so the next event's
 *    range resolves against stale mappings — slate silently falls back to its
 *    internal selection and the replacement is inserted at the wrong place
 *    WITHOUT deleting the original text ("…it were sent yesterday.was").
 * 2. The paste pipeline treats the payload as document content: a multi-line
 *    or HTML payload is deserialized into block elements that split the
 *    paragraph mid-sentence.
 *
 * Both were reproduced against the note editor (2026-09, customer report of
 * Grammarly "restructuring sentences and inserting large blocks").
 *
 * ## What this plugin does instead
 *
 * A replacement is flowing text, not a paste:
 *
 * 1. Resolve the event's target range via the DOM mapping, then VALIDATE it —
 *    the model text at the resolved range must equal the DOM text the range
 *    spans.
 * 2. On mismatch or failure, fall back to text-anchored resolution: locate
 *    the replaced string inside its block by content, choosing the occurrence
 *    nearest the range's DOM offset (robust against same-frame staleness).
 * 3. Apply the payload's `text/plain` as an inline `insertText` at the range
 *    — never through HTML deserialization — with newlines collapsed to
 *    spaces, so a replacement can never split the paragraph into blocks.
 * 4. If the replaced text cannot be located in the current model at all, DROP
 *    the event: a suggestion that fails to apply beats corrupted content.
 *
 * Payloads that are not plain-text replacements (files, in-app slate
 * fragments) are left to the default pipeline untouched.
 */

/** In-app clipboard type slate stamps on copies; never a text replacement. */
const SLATE_FRAGMENT_MIME = 'application/x-slate-fragment';

/**
 * The subset of `StaticRange` the handler reads. Structural so tests (and
 * jsdom, which lacks a `StaticRange` constructor) can supply plain objects.
 */
export interface StaticRangeLike {
  startContainer: Node;
  startOffset: number;
  endContainer: Node;
  endOffset: number;
}

/** The subset of `DataTransfer` the handler reads. */
interface ReplacementDataTransfer {
  readonly files: { readonly length: number };
  readonly types: readonly string[];
  getData: (format: string) => string;
}

/**
 * The subset of the native `InputEvent` the handler reads. `InputEvent`
 * satisfies this structurally.
 */
export interface ReplacementBeforeInputEvent {
  readonly inputType: string;
  readonly dataTransfer: ReplacementDataTransfer | null;
  getTargetRanges: () => readonly StaticRangeLike[];
  preventDefault: () => void;
}

/**
 * Newlines collapsed to single spaces: a replacement is flowing text within a
 * sentence — block/line structure must come from the user, never from a
 * suggestion payload (that is the "inserting large blocks" bug).
 */
export function normalizeReplacementText(text: string): string {
  return text.replace(/[^\S\r\n]*(?:\r\n|\r|\n)+[^\S\r\n]*/g, ' ');
}

/**
 * Index of the occurrence of `needle` in `haystack` closest to
 * `preferredIndex`, or null when absent. Distance is measured from the
 * occurrence's start.
 */
export function nearestOccurrence(
  haystack: string,
  needle: string,
  preferredIndex: number
): number | null {
  if (!needle) {
    return null;
  }
  let best: number | null = null;
  let from = 0;
  for (;;) {
    const i = haystack.indexOf(needle, from);
    if (i === -1) {
      break;
    }
    if (
      best === null ||
      Math.abs(i - preferredIndex) < Math.abs(best - preferredIndex)
    ) {
      best = i;
    }
    from = i + 1;
  }
  return best;
}

/**
 * The text the static range spans, read from the live DOM. Null when the
 * range's nodes are detached or its offsets no longer fit (extensions hold
 * ranges across our re-renders).
 */
function staticRangeText(sr: StaticRangeLike): string | null {
  if (!sr.startContainer.isConnected || !sr.endContainer.isConnected) {
    return null;
  }
  try {
    const range = document.createRange();
    range.setStart(sr.startContainer, sr.startOffset);
    range.setEnd(sr.endContainer, sr.endOffset);
    return range.toString();
  } catch {
    return null;
  }
}

/**
 * Character offset of (container, offset) within `blockEl`'s visible text,
 * mirroring the slate model's string for the block: text inside
 * `[data-slate-zero-width]` spans (slate's placeholder U+FEFF characters) is
 * excluded. Null when `container` is not a text node inside the block.
 */
function domOffsetInBlock(
  blockEl: Element,
  container: Node,
  offset: number
): number | null {
  if (container.nodeType !== Node.TEXT_NODE) {
    return null;
  }
  const walker = document.createTreeWalker(blockEl, NodeFilter.SHOW_TEXT);
  let total = 0;
  let node: Node | null;
  while ((node = walker.nextNode())) {
    const isZeroWidth =
      (node.parentElement?.closest('[data-slate-zero-width]') ?? null) !== null;
    if (node === container) {
      return total + (isZeroWidth ? 0 : offset);
    }
    if (!isZeroWidth) {
      total += (node as Text).data.length;
    }
  }
  return null;
}

/**
 * The point at character offset `target` within the block's concatenated leaf
 * text, or null when the offset runs past the block's end.
 */
function pointAtBlockOffset(
  block: TElement,
  blockPath: Path,
  target: number
): Point | null {
  let remaining = target;
  for (const [textNode, relPath] of NodeApi.texts(block)) {
    const len = textNode.text.length;
    if (remaining <= len) {
      return { offset: remaining, path: [...blockPath, ...relPath] };
    }
    remaining -= len;
  }
  return null;
}

/**
 * Resolve where the replacement applies in the CURRENT model.
 *
 * Order of preference: validated direct DOM→model mapping; text-anchored
 * search within the containing block; null (caller drops the event).
 */
function resolveReplacementRange(
  editor: PlateEditor,
  staticRange: StaticRangeLike | null
): TRange | null {
  if (!staticRange) {
    // No target range on the event: the only trustworthy anchor is an
    // expanded editor selection (what slate itself would use). A collapsed
    // selection cannot describe a replacement target — drop.
    const sel = editor.selection;
    return sel && RangeApi.isExpanded(sel) ? sel : null;
  }

  const oldText = staticRangeText(staticRange);
  // Plate's toSlateRange wrapper always suppresses throws (returns
  // null/undefined for unmappable ranges). Cast: the runtime accepts any
  // StaticRange-shaped object; the declared type only admits the DOM classes.
  const direct = editor.api.toSlateRange(
    staticRange as unknown as globalThis.StaticRange,
    { exactMatch: false, suppressThrow: true }
  );
  if (direct != null && oldText != null) {
    if (oldText === '' && RangeApi.isCollapsed(direct)) {
      // Zero-length target: a pure insertion at a point — nothing to validate
      // against, accept the direct mapping.
      return direct;
    }
    if (oldText !== '' && editor.api.string(direct) === oldText) {
      return direct;
    }
  }
  if (!oldText) {
    return null;
  }

  // Text-anchored fallback: the mapping is stale (a prior replacement in the
  // same frame) or wrong. Find the block element, then locate the replaced
  // string in the CURRENT model text of that block.
  const startEl =
    staticRange.startContainer instanceof Element
      ? staticRange.startContainer
      : staticRange.startContainer.parentElement;
  const blockEl = startEl?.closest('[data-slate-node="element"]');
  if (!blockEl) {
    return null;
  }
  const domNode = editor.api.toSlateNode(blockEl);
  const blockPath = domNode ? editor.api.findPath(domNode) : undefined;
  if (!blockPath) {
    return null;
  }
  const entry = editor.api.node<TElement>(blockPath);
  if (!entry) {
    return null;
  }
  const [block] = entry;
  const blockText = NodeApi.string(block);
  const domOffset = domOffsetInBlock(
    blockEl,
    staticRange.startContainer,
    staticRange.startOffset
  );
  const idx =
    domOffset !== null && blockText.startsWith(oldText, domOffset)
      ? domOffset
      : nearestOccurrence(blockText, oldText, domOffset ?? 0);
  if (idx === null) {
    return null;
  }
  const anchor = pointAtBlockOffset(block, blockPath, idx);
  const focus = pointAtBlockOffset(block, blockPath, idx + oldText.length);
  return anchor && focus ? { anchor, focus } : null;
}

/**
 * Handle one `insertReplacementText` beforeinput event. Returns `true` when
 * the event was consumed (slate's default handling must not run), `undefined`
 * when it is not a plain-text replacement and the default pipeline should
 * proceed.
 */
export function handleInsertReplacementText(
  editor: PlateEditor,
  event: ReplacementBeforeInputEvent
): true | undefined {
  if (event.inputType !== 'insertReplacementText') {
    return undefined;
  }
  const dt = event.dataTransfer;
  if (!dt) {
    return undefined;
  }
  // Files or in-app slate fragments are not text replacements — leave them to
  // the default insertData pipeline.
  if (dt.files.length > 0 || dt.getData(SLATE_FRAGMENT_MIME)) {
    return undefined;
  }
  // `getData` returns '' both for an absent type and for an empty value, so
  // check `types`: an ABSENT text/plain payload is not a text replacement
  // (defer to the default pipeline), while a PRESENT-but-empty one is a valid
  // deletion suggestion (the extension removes the target text) and must be
  // handled here — slate's default path silently drops it.
  if (!dt.types.includes('text/plain')) {
    return undefined;
  }
  const text = dt.getData('text/plain');

  const [targetRange] = event.getTargetRanges();
  event.preventDefault();

  try {
    const at = resolveReplacementRange(editor, targetRange ?? null);
    if (!at) {
      // The replaced text no longer exists where the extension believes it
      // does. Dropping the suggestion is the only non-corrupting option.
      return true;
    }
    // Select-then-insert mirrors slate's own handling: an expanded selection
    // is deleted and the caret ends after the inserted text.
    editor.tf.select(at);
    const replacement = normalizeReplacementText(text);
    if (replacement) {
      editor.tf.insertText(replacement);
    } else if (editor.selection && RangeApi.isExpanded(editor.selection)) {
      // Empty payload over an expanded target = pure deletion (the extension
      // removes the text); insertText('') would be a no-op.
      editor.tf.deleteFragment();
    }
  } catch (e) {
    // preventDefault already ran; consuming a suggestion on an internal error
    // is safer than letting the default pipeline corrupt the document.
    console.error('insertReplacementTextPlugin: failed to apply', e);
  }
  return true;
}

/**
 * Shared plugin — every Plate editor surface must include it (note, email,
 * conversation, event-name template). Registered as a `handlers` plugin so it
 * runs before slate-react's own beforeinput processing; returning `true`
 * stops slate's default handling for the event.
 */
export const insertReplacementTextPlugin = createPlatePlugin({
  key: 'insertReplacementText',
  handlers: {
    onDOMBeforeInput: ({ editor, event }) => {
      // Plate wraps the native event in a React-like synthetic object at
      // runtime (convertDomEventToSyntheticEvent) but types it as Event. The
      // wrapper is built by spreading the native event, which loses prototype
      // members (getTargetRanges, dataTransfer) — unwrap to the native event.
      const native =
        (event as unknown as { nativeEvent?: Event }).nativeEvent ?? event;
      if (!(native instanceof InputEvent)) {
        return undefined;
      }
      return handleInsertReplacementText(editor, native);
    },
  },
});
